/**
 * Partial destruction step 5, the structures' half (ARCHITECTURE §5.13, 63.4's future; decision 141): a marked part that
 * detaches (constants/damage.ts STRUCTURE_PARTS `detaches`: the tower's radar head, the emplacement's shield) falls away
 * when a blast takes it to 0 while its structure stands. It lies as debris on the trees' rules (core/debris.ts): its
 * measured box laid down as a lying piece, out from where it stood and away from the blow by a keyed closed-form fall
 * (a keyed share of its height, a keyed yaw), the cap and the heaps (`settleDebris`), and a light `drop` blow on whoever it
 * lands on, never a kill (`debrisLandsOn`). Its shape left the structure's at 0 (step 4). Pure; with partialDestruction
 * off no structure carries part healths, so nothing here does anything.
 */
import type { DebrisState, GameState, StructureState, Vec3, PropEvent } from '../types';
import type { BlowCause } from '../types/body';
import { DEBRIS } from '../constants';
import { markedParts, partHealth } from './parts';
import { markedPartPiece } from './hits';
import { settleDebris, debrisLandsOn } from './debris';
import { treeFrame, treeFallYaw, propHash } from './treeDraws';
import { applyDamage } from './damage';
import { vecAdd, vecNormalize, vecScale, vecSub } from '../math';

/** The pieces a structure's detaching parts make falling now: those at 0 in `after` that stood in `before`. */
export function partPiecesFalling(state: Pick<GameState, 'planets' | 'config'>, before: StructureState, after: StructureState, blowFrom: Vec3 | undefined, tick: number, seq: number): DebrisState[] {
  if (!after.parts || after.health <= 0) return [];
  const out: DebrisState[] = [];
  markedParts(after.type).forEach((m, k) => {
    if (!m.detaches || after.parts![k] > 0 || (before.parts && before.parts[k] <= 0)) return;
    const piece = markedPartPiece(before, k, state);
    const planet = state.planets[after.planetIndex];
    if (!piece || !planet) return;
    const id = seq + out.length;
    const away = treeFrame(piece.up, treeFallYaw(id, piece.up, piece.foot, blowFrom))(0, 0, 1);
    const reach = (DEBRIS.reach[0] + (DEBRIS.reach[1] - DEBRIS.reach[0]) * propHash(id, 31)) * piece.height;
    const land = vecAdd(piece.foot, vecAdd(piece.across, vecScale(away, reach)));
    out.push({ id, planetIndex: after.planetIndex, dir: vecNormalize(vecSub(land, planet.position)), yaw: propHash(id, 32) * Math.PI * 2, length: piece.length, ra: piece.radius, rb: piece.radius, tick });
  });
  return out;
}

/**
 * After a blast: every standing structure whose marked parts it wore logs a `part` entry per part (partial destruction step
 * 6: the prop log, `state.propEvents`, kind 'structure' — how much the part lost, whether it is gone, whether it fell, where
 * the blow came from), and sheds each detaching part it took to 0 — the pieces join the debris (settled under the cap) and
 * land on whoever is under them. `before` is the structures as they stood before the blast.
 */
export function shedStructureParts(before: readonly StructureState[], state: GameState, blowFrom: Vec3 | undefined, cause: BlowCause = 'blast', by?: number): GameState {
  if (!state.config.partialDestruction) return state;
  const was = new Map(before.map(s => [s.id, s]));
  const tick = state.tick ?? state.turnNumber;
  let shed: DebrisState[] = [];
  const events: PropEvent[] = [];
  for (const s of state.structures) {
    const b = was.get(s.id);
    if (!b || !s.parts || s.parts === b.parts) continue;
    const fell = partPiecesFalling(state, b, s, blowFrom, tick, (state.debrisSeq ?? 0) + shed.length);
    const marked = markedParts(s.type);
    for (let k = 0; k < marked.length; k++) {
      const was_ = partHealth(b, k), now = s.parts[k];
      if (!(was_ - now > 0)) continue;
      const piece = s.health > 0 && marked[k].detaches && now <= 0 ? fell.shift() : undefined;
      if (piece) shed = shed.concat(piece);
      events.push({ tick, cause, kind: 'structure', id: s.id, part: marked[k].part, ...(by !== undefined ? { by } : {}), amount: was_ - now, felled: now <= 0,
        ...(blowFrom ? { from: { x: blowFrom.x, y: blowFrom.y, z: blowFrom.z } } : {}), ...(piece ? { detached: true, debris: [piece.id] } : {}) });
    }
  }
  if (events.length === 0) return state;
  let next: GameState = { ...state, propEvents: [...(state.propEvents ?? []), ...events],
    ...(shed.length ? { debris: settleDebris([...(state.debris ?? []), ...shed], state.planets), debrisSeq: (state.debrisSeq ?? 0) + shed.length } : {}) };
  for (const d of shed) for (const hit of debrisLandsOn(next, d)) next = applyDamage(next, { unit: hit.unitId }, hit.amount, 'drop').state;
  return next;
}

/**
 * Partial destruction step 6, the replay: each structure's marked parts as the log leaves them — its `part` entries
 * (kind 'structure') applied in order to the structures as they stood before (whole where they carry none). What a
 * battle did to a structure's parts persists as the log, as the terrain's and the trees' damage do.
 */
export function partsFromLog(structures: readonly StructureState[], events: readonly PropEvent[]): StructureState[] {
  const lost = new Map<number, Map<number, number>>();
  for (const e of events) {
    if (e.kind !== 'structure' || e.part === undefined) continue;
    const by = lost.get(e.id) ?? new Map<number, number>();
    by.set(e.part, (by.get(e.part) ?? 0) + e.amount);
    lost.set(e.id, by);
  }
  return structures.map(s => {
    const by = lost.get(s.id);
    if (!by) return s;
    const parts = markedParts(s.type).map((m, k) => Math.max(0, partHealth(s, k) - (by.get(m.part) ?? 0)));
    return { ...s, parts };
  });
}
